import type { IEvent } from "@/web_analytics/dataLayer/events/types/types";

declare global {
  var __gtmLoaded: boolean | undefined;
  var __gtmContainerId: string | undefined;
}

// Extract GTM container id from server-injected configuration script
export function getGtmId(): string | undefined {
  const el = document.getElementById("clientConfiguration");
  if (!el) return undefined;
  try {
    const json = JSON.parse(el.textContent ?? "{}");
    return json.gtmContainerId ?? json.GtmContainerId ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Initializes a lazy GTM loader that waits for the first Page Load / Virtual Page Load
 * event to be pushed into dataLayer (or a timeout) before injecting the GTM container.
 * This lets the application emit a fully enriched page load event first.
 */
export function initLazyGtm(containerId: string, timeoutMs = 3000) {
  if (!containerId) return;
  if (globalThis.__gtmLoaded) return; // already loaded
  globalThis.__gtmContainerId = containerId;
  globalThis.dataLayer = globalThis.dataLayer || [];

  const originalPush = globalThis.dataLayer.push.bind(globalThis.dataLayer) as (
    ...items: IEvent[]
  ) => number;
  let triggered = false;

  function loadGtm() {
    if (globalThis.__gtmLoaded) return;
    globalThis.__gtmLoaded = true;
    originalPush({
      "gtm.start": Date.now(),
      event: "gtm.js",
    } as unknown as IEvent);
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://bq1.if.fi/gtm.js?id=${encodeURIComponent(containerId)}`;
    const n = document.querySelector("[nonce]");
    if (n) {
      const possibleNonce =
        (n as HTMLElement & { nonce?: string }).nonce ??
        n.getAttribute("nonce");
      if (possibleNonce) s.setAttribute("nonce", possibleNonce);
    }
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode?.insertBefore(s, firstScript);
  }

  function maybeTriggerLoad(obj: IEvent) {
    if (triggered) return;
    if (
      obj &&
      typeof obj.event === "string" &&
      (obj.event === "Page Load" ||
        obj.event === "Virtual Page Load" ||
        obj.event === "Product Page Impression")
    ) {
      triggered = true;
      loadGtm();
    }
  }

  // Patch push to observe future events
  globalThis.dataLayer.push = ((...items) => {
    for (const it of items) maybeTriggerLoad(it as IEvent);
    return originalPush(...(items as IEvent[]));
  }) as typeof globalThis.dataLayer.push;

  // Also scan existing pre-populated entries (unlikely but safe)
  globalThis.dataLayer.forEach((o) => {
    maybeTriggerLoad(o as IEvent);
  });

  // Fallback timeout to ensure GTM eventually loads
  setTimeout(() => {
    if (!globalThis.__gtmLoaded) {
      loadGtm();
    }
  }, timeoutMs);
}
